import { pinia } from '@/main.js';
import { defineStore } from "pinia";
import { createApp } from "vue";
import * as quasar from 'quasar';
import { useAssetStore } from "@/stores/assetStore";
import { useForecastStore } from "@/stores/forecastStore";
import { useSettingStore } from '@/stores/settingStore';
import { useFeatureFlagStore } from '@/stores/featureFlagStore';
import { env } from "@/env.js";
import {
    getAllAssetWatersheds,
    getGaugeDataByGaugeId,
    getVirtualGaugeDataByGaugeId,
    getGaugeDataByAssetId,
    getVirtualGaugeDataByAssetId,
    getGaugeWatershedById,
    getVirtualGaugeWatershedById,
    getModelCoveragePolygons,
    getWatershedByAssetId,
    getGaugesAsGeoJSON,
    getAvailablePrecipTiles,
    getMaxFireYear
} from "@/utils/api.js";
import { createPopupHtml, getAssetGeojson, getGaugeGeojson, mapLabelList, mapWaterLabelList } from "@/utils/mapHelpers.js";
import {
    assetLayer,
    assetTextLayer,
    gaugeLayer,
    gaugeTextLayer,
    emptyMapData,
    fireYears,
    latestFireYear,
    assetHighlightLayer,
    gaugeHighlightLayer,
    modelPrecipMapTiles,
    overlayFillLayer,
    overlayLineLayer,
    overlayPointLayer,
    loadingSvg,
} from "@/utils/constants.js";
import { addDaysToDate } from '@/utils/dateHelpers.js';
import { getOffset } from "@/utils/unitHelpers";

import * as maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

import StreamflowChart from "@/components/home/asset-drawer/StreamflowChart.vue";

let clickCoords = {};

export const defaultState = () => ({
    map: null,
    gaugesGeoJSON: null,
    initialized: false,
    activeAssetPopup: new maplibregl.Popup({
        closeOnClick: false,
        className: 'popup-active',
        offset: [0, -12],
        anchor: "bottom",
    }),
    activeGaugePopup: new maplibregl.Popup({
        className: 'gauge-popup',
        closeOnClick: false,
        offset: [0, -20],
        anchor: "bottom",
    }),
    activeGauge: null, // The gauge the user has clicked on
    allWatershedsFetched: false,
    showWatersheds: {
        all: false,
        active: false,
    },
    showGauges: {
        all: true,
        noRps: true, // No RPs available
        noData: true, // No current data
        network: {
            MSC: true,
            CEHQ: true,
            USGS: true,
            Floodhub: true,
        },
        configured: true,
        unconfigured: false,
        watershed: false,
    },
    latestFireYear: 0,
    showFires: {
        all: false,
        0: false,
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
    },
    showModels: {
        all: false,
        nwm: false,
        hrdps: false,
        ecmwf: false,
        hrrr: false,
        ukmo: false,
        rdps: false,
    },
    modelsLoaded: false,
    showOverlays: {},
    // Used for precip map
    currentLayer: 0,
    previousLayer: 0,
    initialOffset: 0,
    showPrecipLayer: false,
    currentPrecipMapModel: null,
    isPaused: true,
    totalLayers: 25,
    // Used for admin mode
    newAssetCoordinates: { lng: 0, lat: 0 },
    acceptMapClick: false,
    acceptFwaClick: false,
    clickedStreamId: -1,
    hoveredStreamId: -1,
    newAssetMarker: new maplibregl.Marker(),
});

export const useMapStore = defineStore("map", {
    state: defaultState,
    getters: {
        mapPadding() {
            const settingStore = useSettingStore();
            if (settingStore.adminSidebarCollapse) return 250;
            const assetStore = useAssetStore();
            return (assetStore.drawerOpen && !settingStore.adminMode) || assetStore.createAssetDrawerOpen || settingStore.adminAction !== '' ? 600 : 0;
        },
    },
    actions: {
        /**
         * Initialize empty asset layers and add functionality
         */
        addEmptyLayers(deepState) {
            const assetStore = useAssetStore();

            // Add Misc Layers
            this.addStreamLayerToMap();
            this.addFireLayerToMap();
            this.addModelLayersToMap();

            // Add Watershed Layers
            this.addWatershedLayerToMap('watershed');
            this.addWatershedLayerToMap('gauge-watershed');
            this.addWatershedLayerToMap('new-watershed');

            // Gauge Layers
            if (!this.map.getSource('gauge-source')) {
                this.map.addSource('gauge-source', getGaugeGeojson([]));
            }
            if (!this.map.getLayer('gauge-text-layer')) {
                this.map.addLayer(gaugeTextLayer);
            }
            if (!this.map.getLayer('gauge-layer')) {
                this.map.addLayer(gaugeLayer, 'gauge-text-layer');
            }
            if (!this.map.getLayer('gauge-highlight-layer')) {
                this.map.addLayer(gaugeHighlightLayer, 'gauge-layer');
            }

            // Asset Layers
            if (!this.map.getSource('asset-source')) {
                this.map.addSource('asset-source', getAssetGeojson([]));
            }
            if (!this.map.getLayer('asset-text-layer')) {
                this.map.addLayer(assetTextLayer);
            }
            if (!this.map.getLayer('asset-highlight-layer')) {
                this.map.addLayer(assetHighlightLayer, 'asset-text-layer');
            }
            if (!this.map.getLayer('asset-layer')) {
                this.map.addLayer(assetLayer, 'asset-highlight-layer');
            }

            // Add Asset & Gauge Layer Events
            const hoverPopup = new maplibregl.Popup({
                className: 'popup-hover',
                closeButton: false,
                closeOnClick: false,
                offset: [0, -8],
                anchor: "bottom",
            });

            this.map.on('mouseenter', 'asset-layer', (ev) => {
                this.map.getCanvas().style.cursor = 'pointer';
                const location = this.map.queryRenderedFeatures(ev.point, { layers: ['asset-layer']})[0];

                if (!location || !location.properties) {
                    hoverPopup.remove();
                    return;
                } else if (location.properties.id === assetStore.activeAssetId) {
                    return;
                }

                const popupTitle = `${location.properties.name}${location.properties.status === 'Pending' ? ' <i>(Pending)</i>' : ''}`
                const popHtml = createPopupHtml(popupTitle, "asset");
                hoverPopup.setLngLat(location.geometry.coordinates).setHTML(popHtml).addTo(this.map);
                if (assetStore.groupMap[assetStore.activeGroupId].assetIds.includes(location.properties.id)) {
                    hoverPopup.addClassName('popup-active');
                } else {
                    hoverPopup.removeClassName('popup-active');
                }
            });
            this.map.on('mouseleave', 'asset-layer', () => {
                this.map.getCanvas().style.cursor = '';
                hoverPopup.remove();
            });

            this.map.on('mouseenter', 'gauge-layer', () => {
                this.map.getCanvas().style.cursor = 'pointer';
            });
            this.map.on('mouseleave', 'gauge-layer', () => {
                this.map.getCanvas().style.cursor = '';
            });

            this.map.on('click', ['asset-layer', 'gauge-layer'], async (ev) => {
                const clickedAsset = this.map.queryRenderedFeatures(ev.point, { layers: ['asset-layer']})[0];
                const clickedGauge = this.map.queryRenderedFeatures(ev.point, { layers: ['gauge-layer']})[0];
                if (clickedAsset) {
                    assetStore.activeAssetId = clickedAsset.properties.id;
                    if (deepState.getState('asset') != clickedAsset.properties.id) {
                        deepState.setState('asset', +clickedAsset.properties.id);
                    }
                    if (!clickedAsset.properties.groupIds.includes(assetStore.activeGroupId)) {
                        assetStore.setActiveGroup(clickedAsset.properties.groupIds[0], deepState);
                    }
                    this.setActiveAssetPopup();
                    hoverPopup.remove();
                    assetStore.drawerOpen = true;
                    assetStore.chartMode = 'single';
                } else if (clickedGauge) {
                    if (this.activeGaugePopup) {
                        if (clickedGauge.properties.id === this.activeGauge?.id) {
                            const chartEl = document.getElementsByClassName('chart-popup');
                            if (chartEl.length > 0) return;
                        }
                    }
                    if (clickedGauge.properties.con) {
                        assetStore.activeAssetId = clickedGauge.properties.aid;
                        assetStore.activeGroupId = assetStore.activeAsset.groupIds[0];
                    }
                    // add a highlight, fly to this gauge
                    this.activeGauge = {
                        gaugeName: clickedGauge.properties.nm || `Unnamed Virtual Gauge: hybas_${clickedGauge.properties.oid}`,
                        isVirtual: clickedGauge.properties.v,
                        id: clickedGauge.properties.id,
                        coordinates: ev.lngLat,
                        configured: clickedGauge.properties.con,
                        color: clickedGauge.properties.c,
                        network: clickedGauge.properties.nt,
                        hasReturnPeriods: clickedGauge.properties.rp,
                        aid: clickedGauge.properties.aid,
                    };
                    await this.setGaugeLayerFilter();
                    if (this.showGauges.watershed) await this.setActiveGaugeWatershed();

                    setTimeout(() => {
                        if (window.screen.width < 851) {
                            this.map.easeTo({
                                center: clickedGauge.geometry.coordinates,
                                offset: [0, 0],
                            });
                        }
                    });

                    // set content for the popup with a button to show hydrograph
                    const divElement = document.createElement('div');
                    divElement.className = 'gauge-popup gauge-popup-header q-pa-sm';
                    divElement.innerHTML = this.activeGauge.gaugeName;
                    const hydrographBtn = document.createElement('div');
                    hydrographBtn.className = 'cursor-pointer text-center popup-hydro-button';
                    hydrographBtn.innerHTML = `Hydrograph`;
                    divElement.appendChild(hydrographBtn);
                    hydrographBtn.addEventListener('click', async () => {
                        hydrographBtn.className = '';
                        hydrographBtn.innerHTML = '';
                        const svgContainer = document.createElement('div');
                        svgContainer.className = 'text-center'
                        const doc = new DOMParser().parseFromString(loadingSvg, 'application/xml');
                        doc.className= 'text-center'
                        svgContainer.appendChild(svgContainer.ownerDocument.importNode(doc.documentElement, true));
                        divElement.appendChild(svgContainer)
                        await this.showHydrographInPopup(clickedGauge.properties.aid);
                        this.activeGaugePopup.addClassName('hydro-popup');
                    });

                    this.activeGaugePopup.setLngLat(clickedGauge.geometry.coordinates).setDOMContent(divElement).addTo(this.map);
                }
            });
        },
        /**
         * Set Asset & User Gauge source using all assets in assetStore
         */
        async resetMapSource(resetGauges=true) {
            const assetStore = useAssetStore();
            if (assetStore.loadStatus !== 'success') return;

            // Set asset locations
            if (this.map.getSource('asset-source')) {
                this.map.getSource('asset-source').setData(getAssetGeojson(assetStore.allAssets).data);
                this.setActiveAssetPopup();
            }

            // Set gauge locations
            if (resetGauges) {
                assetStore.handleGaugeResponse(await getGaugesAsGeoJSON());
            }
            if (this.map.getSource('gauge-source') && assetStore.gaugesGeoJSON) {
                const gaugeData = getGaugeGeojson(assetStore.gaugesGeoJSON.features).data;
                this.map.getSource('gauge-source').setData(gaugeData);
                await this.setGaugeLayerFilter();
            }
        },
        toggleSatelliteMode(satelliteOn) {
            this.map.setLayoutProperty('satellite', 'visibility', satelliteOn ? 'visible' : 'none');
            mapLabelList.forEach(el => {
                this.map.setPaintProperty(`${el} labels`, 'text-color', satelliteOn ? '#fff' : '#000');
                this.map.setPaintProperty(`${el} labels`, 'text-halo-color', satelliteOn ? '#000' : '#fff');
            });
            mapWaterLabelList.forEach(el => {
                this.map.setPaintProperty(el, 'text-color', satelliteOn ? '#87ceeb' : '#1C74AB');
                this.map.setPaintProperty(el, 'text-halo-color', satelliteOn ? '#000' : '#fff');
            });
        },

        // Asset Specific Stuff
        /**
         * Set active asset popup and change highlight layers
         */
        setActiveAssetPopup() {
            const assetStore = useAssetStore();
            if (!assetStore.activeAsset) return;
            const popupTitle = `${assetStore.activeAsset.name}${assetStore.activeAsset.status === 'Pending' ? ' <i>(Pending)</i>' : ''}`;
            const popHtml = createPopupHtml(popupTitle, "asset");
            this.activeAssetPopup.setLngLat(assetStore.activeAsset.location.coordinates).setHTML(popHtml).addTo(this.map);
            this.setActiveGaugeByActiveAsset();

            if (this.map.getLayer('asset-highlight-layer')) {
                this.map.setFilter('asset-highlight-layer', ['==', 'id', assetStore.activeAssetId]);
            }
        },
        /**
         * Set map zoom and position to encompass all assets within the active group
         */
        fitMapToGroup() {
            const assetStore = useAssetStore();
            if (!assetStore.activeGroup) return;
            const assetCoords = assetStore.activeGroup.assetIds.map(id => assetStore.assetMap[id].location.coordinates);
            const bounds = assetCoords.reduce(
                (curr, coord) => curr.extend(coord),
                new maplibregl.LngLatBounds(assetCoords[0], assetCoords[0])
            );
            setTimeout(() => {
                this.map?.fitBounds(bounds, {
                    padding: 50,
                    maxZoom: 11,
                    duration: 1500,
                });
            }, 1000)
        },
        /**
         * Set the asset layer filter based on assetStore's filtered values
         */
        async setAssetFilters() {
            if (!this.initialized) return;
            const assetStore = useAssetStore();
            if (!assetStore.filteredMapAssetIds.includes(assetStore.activeAssetId)) {
                this.activeAssetPopup.remove();
                this.map.setFilter('asset-highlight-layer', false);
            } else {
                this.map.setFilter('asset-highlight-layer', ['==', 'id', assetStore.activeAssetId]);
            }
            this.map.setFilter('asset-layer', ['in', 'id', ...assetStore.filteredMapAssetIds]);
            this.map.setFilter('asset-text-layer', ['all', ['in', 'id', ...assetStore.filteredMapAssetIds], ['>', 'forecastRiskLevel', 0]]);

            // Update watershed layers
            this.setAssetWatershed();

            // Update Gauge filter
            await this.setGaugeLayerFilter();
        },

        // Gauge Specific Stuff
        /**
         * Sets the active gauge to be the gauge of the active asset
         */
        async setActiveGaugeByActiveAsset() {
            const assetStore = useAssetStore();
            if (assetStore.createAssetDrawerOpen) return;
            if (assetStore.activeAsset?.id !== this.activeGauge?.aid) this.closeGaugePopup();
            if (assetStore.activeAsset?.hasGauge) {
                this.activeGauge = {
                    gaugeName: assetStore.activeAsset.gaugeData.gaugeName,
                    isVirtual: assetStore.activeAsset.gaugeData.isVirtual,
                    id: assetStore.activeAsset.gaugeData.gaugeId,
                    coordinates: assetStore.activeAsset.gaugeData.gaugeLocation.coordinates,
                    configured: true,
                    network: assetStore.activeAsset.gaugeData.gaugeNetwork,
                    riskLevel: assetStore.activeAsset.gaugeData.riskLevel,
                    hasReturnPeriods: true,
                    aid: assetStore.activeAssetId,
                };
                await this.setGaugeLayerFilter();
            } else {
                this.activeGauge = null;
                this.map.setFilter('gauge-highlight-layer', false);
            }
            this.setActiveGaugeWatershed();
        },
        /**
         * Use active filters to determine which gauges to display
         * @param {Boolean} configuredOnly We only need to recalculate unconfigured gauges when updating filter
         */
        async setGaugeLayerFilter() {
            const assetStore = useAssetStore();
            const gaugeFilter = ['all'];

            // Configured / Unconfigured
            const confFilter = ['any'];
            if (this.showGauges.configured) {
                confFilter.push(['in', 'aid', ...assetStore.filteredMapAssetIds]);
            }
            if (this.showGauges.unconfigured) {
                confFilter.push(['==', 'con', false]);
                if (!assetStore.allGaugesLoaded) {
                    assetStore.allGaugesLoaded = true;
                    await this.resetMapSource();
                }
            }
            gaugeFilter.push(confFilter);

            // Colours
            const colorsToShow = ['#777777'];
            const settingStore = useSettingStore();
            settingStore.gaugeLevels.forEach(el => {
                if (!(el.riskLevel in this.showGauges)) this.showGauges[el.riskLevel] = true;
                if (this.showGauges[el.riskLevel]) colorsToShow.push(el.color);
            });
            if (this.showGauges.noData) colorsToShow.push('#d9d9d9');
            gaugeFilter.push(['in', 'c', ...colorsToShow]);

            if (!this.showGauges.noRps) {
                gaugeFilter.push(['==', 'rp', true]);
            }

            // Network Filter
            const networkFilter = [];
            Object.keys(this.showGauges.network).forEach(el => {
                if (this.showGauges.network[el]) networkFilter.push(el.replace('Floodhub', 'Fh'));
            });
            gaugeFilter.push(['in', 'nt', ...networkFilter]);

            // Close gauge popup and remove watershed if active gauge isn't within the filter
            if (this.activeGauge) {
                const configureCheck = this.activeGauge.configured ? this.showGauges.configured : this.showGauges.unconfigured;
                const networkCheck = this.showGauges.network[this.activeGauge.network?.replace('Fh', 'Floodhub')];
                const riskCheck = (this.showGauges[this.activeGauge.riskLevel] || colorsToShow.includes(this.activeGauge.color)) && (this.showGauges.noRps || this.activeGauge.hasReturnPeriods);
                const assetCheck = !this.activeGauge.configured || assetStore.filteredMapAssetIds.includes(this.activeGauge.aid);

                if (!(configureCheck && networkCheck && riskCheck && assetCheck)) {
                    this.activeGauge = null;
                    this.closeGaugePopup();
                    this.setActiveGaugeWatershed();
                    this.map.setFilter('gauge-highlight-layer', false);
                } else {
                    this.map.setFilter('gauge-highlight-layer', [...gaugeFilter, ['==', 'id', this.activeGauge?.id], ['==', 'v', this.activeGauge?.isVirtual]]);
                }
            } else {
                this.map.setFilter('gauge-highlight-layer', false);
            }

            this.map.setFilter('gauge-layer', gaugeFilter);
            this.map.setFilter('gauge-text-layer', gaugeFilter);
        },
        async showHydrographInPopup(assetId) {
            const chartNode = document.createElement('div');
            chartNode.className = 'q-pa-sm chart-popup';

            let gaugeChartData;
            if (assetId) {
                if (this.activeGauge.isVirtual) {
                    gaugeChartData = await getVirtualGaugeDataByAssetId(assetId);
                } else {
                    gaugeChartData = await getGaugeDataByAssetId(assetId);
                }
            } else {
                if (this.activeGauge.isVirtual) {
                    gaugeChartData = await getVirtualGaugeDataByGaugeId(this.activeGauge.id);
                } else {
                    gaugeChartData = await getGaugeDataByGaugeId(this.activeGauge.id);
                }
            }
            gaugeChartData.gaugeId = this.activeGauge.id;
            const removeChartOnMobile = () => {
                const chartEl = document.getElementsByClassName('chart-popup')[0];
                this.closeGaugePopup();
                if (chart) {
                    chart.unmount();
                }
                if (chartEl) {
                    chartEl.remove();
                }
            };

            const chart = createApp(StreamflowChart, {
                chartData: gaugeChartData,
                chartType: 'tooltip',
                onClose: () => removeChartOnMobile()
            });

            chart.use(quasar.Quasar);
            chart.use(pinia);
            chart.mount(chartNode);
            if (window.gtag) window.gtag('event', 'map', { action: 'show_hydrograph', label: 'Open Hydrograph in Map Popup' });

            if (window.screen.width < 851) {
                document.getElementById('forecast-chart').appendChild(chartNode);
            } else {
                this.map.easeTo({
                    center: this.activeGauge.coordinates,
                    offset: [0, 250],
                });
                this.activeGaugePopup.setDOMContent(chartNode);
            }
        },
        closeGaugePopup() {
            if (this.activeGaugePopup) {
                this.activeGaugePopup.remove();
            }
        },

        // Watershed Layers
        /**
         * Add layers for watershed, gauge-watershed and new-watershed
         * @param {string} name base name for layer and source
         */
        addWatershedLayerToMap(name) {
            if (!this.map.getSource(`${name}-source`)) {
                this.map.addSource(`${name}-source`, {
                    type: 'geojson',
                    data: emptyMapData,
                });
            }
            if (this.map?.getLayer(`${name}-layer`)) return;
            this.map.addLayer({
                id: `${name}-layer`,
                type: 'fill',
                source: `${name}-source`,
                paint: {
                    'fill-color': '#0000ff',
                    'fill-opacity': 0.25,
                },
            });
        },
        async setAssetWatershed() {
            const assetStore = useAssetStore();
            const forecastStore = useForecastStore();
            const myFeatures = [];
            const myData = this.map.getSource('watershed-source');
            if (!myData) return;

            let assetIds = [];
            if (this.showWatersheds.all) {
                assetIds = assetStore.filteredMapAssetIds;
            } else if (this.showWatersheds.active && assetStore.filteredMapAssetIds.includes(assetStore.activeAssetId) && assetStore.activeAsset.hasForecasts) {
                assetIds = [assetStore.activeAssetId];
            }

            if (assetIds.length > 0) {
                if (this.showWatersheds.all && !this.allWatershedsFetched) {
                    const allAssetWatershedsDict = await getAllAssetWatersheds();
                    Object.keys(allAssetWatershedsDict).forEach(id => {
                        forecastStore.watersheds[id] = allAssetWatershedsDict[id].watershedGeojson.coordinates[0]; // Watershed data
                    });
                    this.allWatershedsFetched = true;
                } else if (!forecastStore.watersheds[assetStore.activeAssetId]) {
                    const myWatershed = await getWatershedByAssetId(assetStore.activeAssetId);
                    if (!myWatershed.watershedGeojson) {
                        this.showWatersheds.active = false;
                        forecastStore.watersheds[assetStore.activeAssetId] = null;
                    } else {
                        forecastStore.watersheds[assetStore.activeAssetId] = myWatershed.watershedGeojson.coordinates[0];
                    }
                }

                assetIds.forEach((id) => {
                    if (forecastStore.watersheds[id]) {
                        myFeatures.push({
                            geometry: {
                                coordinates: forecastStore.watersheds[id], // Use stored data
                                type: "Polygon"
                            },
                            type: "Feature"
                        });
                    }
                });

                myData.setData({
                    type: "FeatureCollection",
                    features: myFeatures
                });
            } else {
                myData.setData(emptyMapData);
            }
        },
        async setActiveGaugeWatershed() {
            const myData = this.map.getSource('gauge-watershed-source');
            if (!myData) return;
            try {
                if (this.activeGauge && this.showGauges.watershed) {
                    let gaugeWatershed;
                    if (this.activeGauge.isVirtual) {
                        gaugeWatershed = await getVirtualGaugeWatershedById(this.activeGauge.id);
                    } else {
                        gaugeWatershed = await getGaugeWatershedById(this.activeGauge.id);
                    }
                    if (gaugeWatershed.watershed === 'None') {
                        myData.setData(emptyMapData);
                    } else {
                        myData.setData({
                            type: "FeatureCollection",
                            features: [
                                {
                                    geometry: {
                                        coordinates: gaugeWatershed.watershed.coordinates,
                                        type: "Polygon"
                                    },
                                    type: "Feature"
                                }
                            ]
                        });
                    }
                } else {
                    myData.setData(emptyMapData);
                }
            } catch {
                myData.setData(emptyMapData);
            }
        },

        // Precip Viewer
        async addPrecipTilesToMap(modelName) {
            const settingStore = useSettingStore();
            const availableTiles = await getAvailablePrecipTiles(modelName);
            this.isPaused = true;
            this.currentPrecipMapModel = modelName;
            const timezoneOffset = getOffset();

            // Remove Existing Layers
            this.map.getStyle().layers.forEach((layer) => {
                if (layer.id.startsWith('raster-layer-')) {
                    this.map.removeLayer(layer.id);
                    this.map.removeSource(layer.source);
                }
            });

            const allLayers = Array.from({length: modelPrecipMapTiles[modelName].daysAvailable * 24}, (_, i) => i);
            let firstLayer = -1;
            let lastLayer;
            let hourOffset = 0;

            const localDate = new Date();
            const utcDateWithSameTime = new Date(
                localDate.getTime() + ((localDate.getTimezoneOffset()) * 60000)
            );

            allLayers.forEach(el => {
                const storageSource = ((el - timezoneOffset) % 24).toString().padStart(2, '0');
                const precipLayer = `${addDaysToDate(new Date(), Math.floor((el - timezoneOffset) / 24)).toISOString().split('T')[0]}T${storageSource}:00:00`;
                if (!availableTiles.includes(`${precipLayer}+00:00`)) {
                    hourOffset += 1;
                    if (!lastLayer || hourOffset > 5) {
                        return;
                    }
                } else {
                    if (firstLayer < 0) firstLayer = el
                    lastLayer = precipLayer;
                    if (new Date(utcDateWithSameTime) > new Date(new Date(precipLayer).toLocaleString("en-US", { timezone: settingStore.timezone}))) {
                        firstLayer = el;
                    }
                    hourOffset = 0;
                }

                const rainrate = el.toString().padStart(2, '0');
                const sourceId = `raster-source-${rainrate}`;
                const layerId = `raster-layer-${rainrate}`;
                const precipTilesBaseUrl = env.VITE_PRECIP_TILES_BASE_URL;
                this.map.addSource(sourceId, {
                    'type': 'raster',
                    'tiles': [`${precipTilesBaseUrl}/${modelName}/tiles/rainrate${lastLayer}/{z}/{x}/{y}.png`],
                    'scheme': 'tms',
                    'minzoom': modelPrecipMapTiles[modelName].minZoom,
                    'maxzoom': modelPrecipMapTiles[modelName].maxZoom,
                    'bounds': modelPrecipMapTiles[modelName].bounds
                });

                this.map.addLayer({
                    'id': layerId,
                    'type': 'raster',
                    'source': sourceId,
                    "paint": {
                        "raster-opacity": 0, // Set initial opacity to make the layers visible
                        "raster-opacity-transition": { 'duration' : 500}, // Add opacity transition
                        "raster-fade-duration": 250
                    },
                    'minzoom': 0,
                    'maxzoom': 24
                });
            });

            if (firstLayer < 0) {
                firstLayer = allLayers.length;
                quasar.Notify.create({message: `No data found for ${modelName.toUpperCase()} at this time`, type: 'negative'});
            }
            this.currentLayer = firstLayer;
            this.initialOffset = firstLayer;
            this.totalLayers = allLayers.length;
            if (window.gtag) window.gtag('event', 'layers_drawer', { action: 'toggle_layer', label: `Toggle Precip Layer ${modelName}` });
        },
        async toggleTiles () {
            const delay = 200;

            while (!this.isPaused) {
                await new Promise(resolve => setTimeout(resolve, 40));
                await new Promise(resolve => setTimeout(async () => {
                    if (!this.isPaused) {
                        if (this.currentLayer >= this.totalLayers) {
                            this.currentLayer = this.initialOffset;
                            this.isPaused = true;
                        } else {
                            this.currentLayer += 1;
                        }
                    }
                    resolve(); // Resolve the promise to signal completion
                }, delay));
            }
        },
        generateDatesForModels() {
            const date = new Date();
            if (this.currentPrecipMapModel === null) {
                return [date.toLocaleDateString('en-us', {month: 'short', day: 'numeric'})];
            }
            const numberOfDays = modelPrecipMapTiles[this.currentPrecipMapModel].daysAvailable;
            var availableDates = [];
            for (var i = 0; i < numberOfDays; i++) {
                availableDates.push({label: date.toLocaleDateString('en-us', {month: 'short', day: 'numeric'}), value: new Date(date.getTime())});
                date.setDate(date.getDate() + 1);
            }
            return availableDates;
        },
        async setActiveTile() {
            // Hide Old Layer
            const oldRainrate = this.previousLayer.toString().padStart(2, '0'); // Generate rainrate number with leading zeros
            const oldLayerId = `raster-layer-${oldRainrate}`;
            if (this.map.getLayer(oldLayerId)) {
                this.map?.setPaintProperty(oldLayerId, 'raster-opacity', 0.0);
            }

            const rainrate = this.currentLayer.toString().padStart(2, '0'); // Generate rainrate number with leading zeros
            const layerId = `raster-layer-${rainrate}`;
            // Show Specific Layer
            if (this.map.getLayer(layerId)) {
                this.map?.setPaintProperty(layerId, 'raster-opacity', 0.4);
            }
            this.previousLayer = this.currentLayer;
        },

        // Forest Fires
        async addFireLayerToMap() {
            if (this.map?.getLayer('fire-layer')) return;

            const featureFlagStore = useFeatureFlagStore();
            let fireUrl = 'pmtiles://https://fluxtilesources.blob.core.windows.net/firesbyyear/recent_fires.pmtiles?sp=r&st=2026-02-11T21:56:49Z&se=2032-02-12T06:11:49Z&spr=https&sv=2024-11-04&sr=b&sig=0ReXhyrNxqPFNriNkV%2B1C%2B522pwhhDx5PzLSGaXc%2BkU%3D';
            let sourceLayer = 'recent_fires';
            this.latestFireYear = latestFireYear;
            if(featureFlagStore.featureFlags.USE_NEW_FIRES_DATA) {
                const maxFireYearResp = await getMaxFireYear();
                this.latestFireYear = maxFireYearResp.maxYear;
                fireUrl = `${env.VITE_FIRE_STORAGE_BASE_URL}/output.pmtiles?${env.VITE_FIRE_STORAGE_SAS_TOKEN}`;
                sourceLayer = 'polygons';
            }

            this.map.addLayer({
                id: 'fire-layer',
                type: 'fill',
                source: {
                    type: 'vector',
                    // TODO: will have to make this an env VAR as it'll be diffy per env
                    url: fireUrl,
                },
                'source-layer': sourceLayer,
                paint: {
                    'fill-color': '#FFA500',
                    'fill-opacity': [
                        'interpolate',
                        ['linear'],
                        ['get', 'fire_year'],
                        +this.latestFireYear - 5, 0.2,
                        +this.latestFireYear, 0.6
                    ],
                },
            });
            this.setFireLayerFilter(false);

            const fireLayerPopup = new maplibregl.Popup({
                closeButton: false,
                closeOnClick: false,
                className: 'popup-fire',
                offset: [0, -10],
                anchor: "bottom"
            });

            this.map.on('mousemove', 'fire-layer', (e) => {
                if (e.features && e.features[0].properties) {
                    const { lng, lat } = e.lngLat;
                    const popHtml = createPopupHtml(`${e.features[0].properties.fire_year}`, "fire");
                    fireLayerPopup.setLngLat([lng, lat]).setHTML(popHtml).addTo(this.map);
                }
            });

            this.map.on('mouseleave', 'fire-layer', () => {
                fireLayerPopup.remove();
            });
        },
        setFireLayerFilter(showAll) {
            if (showAll) {
                fireYears.forEach(year => {
                    this.showFires[year] = this.showFires.all;
                });
            }

            const filters = ['any'];
            fireYears.forEach(year => {
                if (this.showFires[year]) filters.push(['==', 'fire_year', this.latestFireYear - year]);
            });
            if (filters.length > 0) {
                this.map.setFilter('fire-layer', filters);
            } else {
                this.map.setFilter('fire-layer', false);
            }
            if (window.gtag) window.gtag('event', 'layers_drawer', { action: 'toggle_layer', label: 'Update Fire Layers' });
        },

        // Forecast Models
        async addModelLayersToMap() {
            if (!this.map.getSource('model-source')) {
                this.map.addSource('model-source', {
                    type: 'geojson',
                    data: emptyMapData,
                });
            }
            if (!this.map.getLayer(`model-layer`)) {
                this.map.addLayer({
                    id: `model-layer`,
                    type: 'fill',
                    source: 'model-source',
                    paint: {
                        'fill-color': ['get', 'color'],
                        'fill-opacity': 0.25,
                    },
                    filter: false,
                });
            }
            if (!this.map.getSource('inverted-model-source')) {
                this.map.addSource('inverted-model-source', {
                    type: 'geojson',
                    data: emptyMapData,
                });
            }
            if (!this.map.getLayer('inverted-model-layer')) {
                this.map.addLayer({
                    id: 'inverted-model-layer',
                    type: 'fill',
                    source: 'inverted-model-source',
                    paint: {
                        'fill-color': 'grey',
                        'fill-opacity': 0.25,
                    },
                    filter: false,
                });
            }
            await this.setModelLayerFilter(false);
        },
        async getModelLayerData() {
            if (this.modelsLoaded) return;
            this.modelsLoaded = true;

            const data = await getModelCoveragePolygons();

            // Deep clone so mutating the inverted copy can't touch the original
            const invertedData = structuredClone(data);
            const outerRing = [[180, -90], [180, 90], [-180, 90], [-180, -90], [180, -90]];
            invertedData.features.forEach(feature => {
                feature.geometry.coordinates.unshift(outerRing);
            });

            // Populate Model Source - Forecast Model Layer - Show Coverage per Model
            this.map.getSource('model-source').setData(data)
            // Populate Inverted Model Source - Hourly Rain Animation - Shade outside of Model Coverage
            this.map.getSource('inverted-model-source').setData(invertedData)
        },
        async setModelLayerFilter(showAll) {
            await this.getModelLayerData();
            const allModels = ['nwm', 'hrdps', 'ecmwf', 'hrrr', 'ukmo', 'rdps'];
            if (showAll) {
                this.map.setFilter('model-layer', this.showModels.all);
                allModels.forEach(key => {
                    this.showModels[key] = this.showModels.all;
                });
            } else {
                const modelsToShow = allModels.filter(model => this.showModels[model]);
                this.map.setFilter('model-layer', ['all', ['in', 'modelName', ...modelsToShow]]);
            }
            if (window.gtag) window.gtag('event', 'layers_drawer', { action: 'toggle_layer', label: 'Update Forecast Model Layer' });
        },
        setInvertedModelLayerFilter() {
            if (this.showPrecipLayer) {
                this.map.setFilter('inverted-model-layer', ['==', 'modelName', this.currentPrecipMapModel]);
            } else {
                this.map.setFilter('inverted-model-layer', false);
            }
        },

        // Stream layers
        /**
         * Adds the fwa layers to map
         */
        addStreamLayerToMap() {
            const paint =  {
                'line-opacity': 1,
                'line-color': [
                    'case',
                    // hovered color:
                    ['boolean', ['feature-state', 'hover'], false],
                    '#50c8c3',
                    // default (not hovered) color:
                    '#5093c3',
                ],
                'line-width': [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    6, ["*", ["get", "order"], 0.0001],
                    24, ["*", ["get", "order"], 1.5]
                ],
            };
            const highlightPaint =  {
                'line-opacity': 0,
                'line-color': 'rgb(0, 0, 0)',
                'line-width': [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    10, 20,
                    12, 50,
                    22, 100
                ],
            };
            if (!this.map.getLayer('fwa-z7-invisible-layer')) {
                this.map.addLayer({
                    id: 'fwa-z7-invisible-layer',
                    type: 'line',
                    source: {
                        type: 'vector',
                        url: 'pmtiles://https://fluxtilesources.blob.core.windows.net/nastream/flux_stream_na_order_7_z9.pmtiles?sp=r&st=2026-02-13T00:13:55Z&se=2032-02-13T08:28:55Z&spr=https&sv=2024-11-04&sr=b&sig=tI6vGL9PyvkX75bweP1jzavy9R5an%2FoJohAareFm79k%3D'
                    },
                    'source-layer': 'flux_stream_na_order_7',
                    minzoom: 0,
                    maxzoom: 7,
                    paint: highlightPaint,
                    layout: { 'line-cap': 'round' },
                });
            }
            if (!this.map?.getLayer('fwa-z7-layer')) {
                this.map.addLayer({
                    id: 'fwa-z7-layer',
                    type: 'line',
                    source: {
                        type: 'vector',
                        url: 'pmtiles://https://fluxtilesources.blob.core.windows.net/nastream/flux_stream_na_order_7_z9.pmtiles?sp=r&st=2026-02-13T00:13:55Z&se=2032-02-13T08:28:55Z&spr=https&sv=2024-11-04&sr=b&sig=tI6vGL9PyvkX75bweP1jzavy9R5an%2FoJohAareFm79k%3D'
                    },
                    'source-layer': 'flux_stream_na_order_7',
                    minzoom: 0,
                    maxzoom: 7,
                    paint,
                    layout: { 'line-cap': 'round' },
                },
                );
            }

            if (!this.map.getLayer('fwa-z9-invisible-layer')) {
                this.map.addLayer({
                    id: 'fwa-z9-invisible-layer',
                    type: 'line',
                    source: {
                        type: 'vector',
                        url: 'pmtiles://https://fluxtilesources.blob.core.windows.net/nastream/flux_stream_na_order_5_z9.pmtiles?sp=r&st=2026-02-12T23:19:28Z&se=2032-02-13T07:34:28Z&spr=https&sv=2024-11-04&sr=b&sig=LW1gCYN2qogPpeCybz0rnlHxkXEGI1PPZrg9ZrfB1J8%3D'
                    },
                    'source-layer': 'flux_stream_na_order_5',
                    minzoom: 7,
                    maxzoom: 9,
                    paint: highlightPaint,
                    layout: { 'line-cap': 'round' },
                });
            }
            if (!this.map?.getLayer('fwa-z9-layer')) {
                this.map.addLayer({
                    id: 'fwa-z9-layer',
                    type: 'line',
                    source: {
                        type: 'vector',
                        url: 'pmtiles://https://fluxtilesources.blob.core.windows.net/nastream/flux_stream_na_order_5_z9.pmtiles?sp=r&st=2026-02-12T23:19:28Z&se=2032-02-13T07:34:28Z&spr=https&sv=2024-11-04&sr=b&sig=LW1gCYN2qogPpeCybz0rnlHxkXEGI1PPZrg9ZrfB1J8%3D'
                    },
                    'source-layer': 'flux_stream_na_order_5',
                    minzoom: 7,
                    maxzoom: 9,
                    paint,
                    layout: { 'line-cap': 'round' },
                },
                );
            }

            if (!this.map?.getLayer('fwa-z10-layer')) {
                this.map.addLayer({
                    id: 'fwa-z10-layer',
                    type: 'line',
                    source: {
                        type: 'vector',
                        url: 'pmtiles://https://fluxtilesources.blob.core.windows.net/nastream/flux_stream_na_order_3_Z9_z11.pmtiles?sp=r&st=2026-02-12T23:18:59Z&se=2032-02-13T07:33:59Z&spr=https&sv=2024-11-04&sr=b&sig=5Bs1fxR%2BZmTCTvn2nwfQHGLjvKbXQqIHxwlSmftG9g0%3D'
                    },
                    'source-layer': 'flux_stream_na_order_3',
                    minzoom: 9,
                    maxzoom: 11,
                    paint,
                    layout: { 'line-cap': 'round' },
                },
                );
            }
            if (!this.map?.getLayer('fwa-z10-invisible-layer')) {
                this.map.addLayer({
                    id: 'fwa-z10-invisible-layer',
                    type: 'line',
                    source: {
                        type: 'vector',
                        url: 'pmtiles://https://fluxtilesources.blob.core.windows.net/nastream/flux_stream_na_order_3_Z9_z11.pmtiles?sp=r&st=2026-02-12T23:18:59Z&se=2032-02-13T07:33:59Z&spr=https&sv=2024-11-04&sr=b&sig=5Bs1fxR%2BZmTCTvn2nwfQHGLjvKbXQqIHxwlSmftG9g0%3D'
                    },
                    'source-layer': 'flux_stream_na_order_3',
                    minzoom: 9,
                    maxzoom: 11,
                    paint: highlightPaint,
                    layout: { 'line-cap': 'round' },
                });
            }

            if (!this.map?.getLayer('fwa-z11-layer')) {
                this.map.addLayer({
                    id: 'fwa-z11-layer',
                    type: 'line',
                    source: {
                        type: 'vector',
                        url: 'pmtiles://https://fluxtilesources.blob.core.windows.net/nastream/stream_geom_all.pmtiles?sp=r&st=2026-02-12T23:16:56Z&se=2032-02-13T07:31:56Z&spr=https&sv=2024-11-04&sr=b&sig=rzyz45ZaSWTkL8L9CDEHrMUibmoDiUkBJyxQOfhpAuw%3D'
                    },
                    'source-layer': 'stream_geom_all',
                    minzoom: 11,
                    paint,
                    layout: { 'line-cap': 'round' },
                },
                );
            }
            if (!this.map?.getLayer('fwa-z11-invisible-layer')) {
                this.map.addLayer({
                    id: 'fwa-z11-invisible-layer',
                    type: 'line',
                    source: {
                        type: 'vector',
                        url: 'pmtiles://https://fluxtilesources.blob.core.windows.net/nastream/stream_geom_all.pmtiles?sp=r&st=2026-02-12T23:16:56Z&se=2032-02-13T07:31:56Z&spr=https&sv=2024-11-04&sr=b&sig=rzyz45ZaSWTkL8L9CDEHrMUibmoDiUkBJyxQOfhpAuw%3D'
                    },
                    'source-layer': 'stream_geom_all',
                    minzoom: 11,
                    paint: highlightPaint,
                    layout: { 'line-cap': 'round' },
                });
            }

            this.map.on('mousemove', ['fwa-z11-invisible-layer', 'fwa-z10-invisible-layer', 'fwa-z9-invisible-layer', 'fwa-z7-invisible-layer'], (e) => {
                let hoveredId = e.features[0]?.id || null;
                if (this.acceptFwaClick && hoveredId) {
                    if (this.hoveredStreamId !== this.clickedStreamId) {
                        this.setFwaHoverState(this.hoveredStreamId, false);
                    }
                    this.hoveredStreamId = hoveredId;
                    this.setFwaHoverState(this.hoveredStreamId, true);
                }
            });
            this.map.on('click', ['fwa-z11-invisible-layer', 'fwa-z10-invisible-layer', 'fwa-z9-invisible-layer', 'fwa-z7-invisible-layer'], (e) => {
                let clickedId = e.features[0]?.id || null;
                if (e.point === clickCoords) {
                    clickCoords = {};
                    return;
                }
                if (this.acceptFwaClick && clickedId) {
                    if (this.clickedStreamId !== -1) {
                        this.setFwaHoverState(this.clickedStreamId, false);
                    }
                    this.clickedStreamId = +clickedId;
                    this.setFwaHoverState(this.clickedStreamId, true);
                }
            });
            this.map.on('mouseenter', ['fwa-z11-invisible-layer', 'fwa-z10-invisible-layer', 'fwa-z9-invisible-layer', 'fwa-z7-invisible-layer'], () => {
                if (this.acceptFwaClick) {
                    this.map.getCanvas().style.cursor = 'pointer';
                }
            });
            this.map.on('mouseleave', ['fwa-z11-invisible-layer', 'fwa-z10-invisible-layer', 'fwa-z9-invisible-layer', 'fwa-z7-invisible-layer'], () => {
                if (this.acceptFwaClick && this.hoveredStreamId !== this.clickedStreamId) {
                    this.setFwaHoverState(this.hoveredStreamId, false);
                }
                this.map.getCanvas().style.cursor = '';
            });

            this.toggleStreamLayerVisibility(false);
        },
        /**
         * Sets the "hover" feature-state of an NHD flow line feature.
         *
         * @param {number} id - ID of the flow line feature
         * @param {string} source - name of layer to filter
         * @param {string} sourceLayer - name of layer to filter
         * @param {boolean} hover - if clicked, save the id
         */
        setHoverState(id, source, sourceLayer, hover) {
            this.map.setFeatureState(
                {
                    id,
                    source,
                    sourceLayer,
                },
                { hover },
            );
        },
        /**
         * Set the hover state of all FWA layers for the designated stream id
         * @param streamId the id of the stream to show/hide
         * @param status determines whether or not to highlight the stream or not
         */
        setFwaHoverState(streamId, status) {
            if (this.acceptMapHover) {
                this.setHoverState(streamId, 'fwa-z7-layer', 'flux_stream_na_order_7', status);
                this.setHoverState(streamId, 'fwa-z9-layer', 'flux_stream_na_order_5', status);
                this.setHoverState(streamId, 'fwa-z10-layer', 'flux_stream_na_order_3', status);
                this.setHoverState(streamId, 'fwa-z11-layer', 'stream_geom_all', status);
            }
        },
        /**
         * Toggle stream layer visibility. Set to be true when user wants to create an asset
         * @param visible determines whether or not to show stream layer or not
         */
        toggleStreamLayerVisibility(visible) {
            const layerList = [
                'fwa-z7-layer',
                'fwa-z9-layer',
                'fwa-z10-layer',
                'fwa-z11-layer',
            ];
            const isVisible = visible ? 'visible' : 'none';
            this.map?.getStyle().layers.forEach(layer => {
                if (layerList.includes(layer.id)) {
                    this.map?.setLayoutProperty(layer.id, 'visibility', isVisible);
                }
            });
        },

        // User Overlay Layer
        async addOverlayLayersToMap() {
            if (!this.map ) return;
            if (!this.map.getSource('overlay-geojson')) {
                const settingStore = useSettingStore();

                const allFeatures = [];

                settingStore.overlays.forEach(layer => {
                    layer.geojson.features.forEach(feature => {
                        feature.properties.name = layer.name;
                        feature.properties.color = layer.color;
                        feature.properties.type = feature.geometry.type;
                        allFeatures.push(feature);
                    });
                    this.showOverlays[layer.name] = true;
                });

                this.map.addSource('overlay-geojson', {
                    type: 'geojson',
                    data: {
                        type: "FeatureCollection",
                        features: allFeatures
                    },
                });
            }
            if (this.map.getSource('overlay-geojson')) {
                this.map.addLayer(overlayLineLayer, 'asset-layer');
                this.map.addLayer(overlayFillLayer, 'asset-layer');
                this.map.addLayer(overlayPointLayer, 'asset-layer');
            }
        },
        setOverlayFilter() {
            const names = [];
            Object.keys(this.showOverlays).forEach(overlay => {
                if (this.showOverlays[overlay])  names.push(overlay);
            });
            this.map.setFilter('overlay-line-layer', ['all', ['in', 'name', ...names], ['==', 'type', 'LineString']]);
            this.map.setFilter('overlay-fill-layer', ['all', ['in', 'name', ...names], ['==', 'type', 'Polygon']]);
            this.map.setFilter('overlay-point-layer', ['all', ['in', 'name', ...names], ['==', 'type', 'Point']]);
            if (window.gtag) window.gtag('event', 'layers_drawer', { action: 'toggle_layer', label: 'Toggle Custom Layer' });
        },
        clear() {
            this.$reset();
        },
    },
});
